<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-engine</v-icon>
            DG Billing Entry
          </v-card-title>
          <v-card-text>
            <p>Create and calculate new diesel generator billing records</p>

            <!-- Progress Stepper -->
            <v-stepper v-model="currentStep" class="mt-4">
              <v-stepper-header>
                <v-stepper-step :complete="currentStep > 1" step="1">
                  Select Period
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 2" step="2">
                  Enter DG Data
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 3" step="3">
                  Calculate Efficiency
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="currentStep > 4" step="4">
                  Review & Save
                </v-stepper-step>
              </v-stepper-header>
            </v-stepper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Step 1: DG Billing Period Selection -->
    <v-row class="mt-4" v-if="currentStep === 1">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-calendar-range</v-icon>
            DG Billing Period Selection
          </v-card-title>

          <v-card-text>
            <v-form ref="periodForm" v-model="periodFormValid">
              <!-- DG Consumption Data Entry -->
              <v-row>
                <v-col cols="12">
                  <BillingPeriodSelector
                    ref="dgPeriodSelector"
                    :edit-mode="false"
                    :is-d-g-mode="true"
                    @dg-consumption-data-retrieved="onDGConsumptionDataRetrieved"
                    @consumption-data-retrieved="onDGConsumptionDataRetrieved"
                    @consumption-data-cleared="onDGConsumptionDataCleared"
                    @show-message="showMessage"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Step 2: Manual DG Data Entry -->
    <v-row class="mt-4" v-if="currentStep === 2">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-form-textbox</v-icon>
            DG Manual Data Entry
          </v-card-title>

          <v-card-text>
            <v-form ref="manualDataForm" v-model="manualDataFormValid">
              <v-row>
                <!-- Diesel Consumption Input -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="manualData.dieselConsumption"
                    label="Diesel Consumption (Litres)"
                    prepend-icon="mdi-gas-station"
                    type="number"
                    step="0.1"
                    min="0"
                    :rules="dieselConsumptionRules"
                    outlined
                    @input="onManualDataChange"
                  >
                    <template #append>
                      <span class="text-caption grey--text">L</span>
                    </template>
                  </v-text-field>
                </v-col>

                <!-- Running Hours Input -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="manualData.runningHours"
                    label="Running Hours"
                    prepend-icon="mdi-clock-outline"
                    type="number"
                    step="0.1"
                    min="0"
                    :rules="runningHoursRules"
                    outlined
                    @input="onManualDataChange"
                  >
                    <template #append>
                      <span class="text-caption grey--text">hrs</span>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Notes Field -->
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="manualData.notes"
                    label="Notes (Optional)"
                    prepend-icon="mdi-note-text"
                    rows="3"
                    outlined
                    counter="500"
                    :rules="notesRules"
                  />
                </v-col>
              </v-row>

              <!-- Input Validation Summary -->
              <v-row v-if="manualDataValidationErrors.length > 0">
                <v-col cols="12">
                  <v-alert
                    type="error"
                    outlined
                  >
                    <div class="subtitle-2 mb-2">Please correct the following errors:</div>
                    <ul>
                      <li v-for="error in manualDataValidationErrors" :key="error">
                        {{ error }}
                      </li>
                    </ul>
                  </v-alert>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Step 3: Efficiency Calculation -->
    <v-row class="mt-4" v-if="currentStep === 3">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-calculator-variant</v-icon>
            DG Efficiency Calculation
          </v-card-title>
          <v-card-text>
            <!-- Real-time Efficiency Metrics -->
            <DGEfficiencyMetrics
              :dg-consumption="dgConsumptionData ? dgConsumptionData.totalConsumption : 0"
              :diesel-consumption="manualData.dieselConsumption"
              :running-hours="manualData.runningHours"
              @efficiency-calculated="onEfficiencyCalculated"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Step 4: Review and Save -->
    <v-row class="mt-4" v-if="currentStep === 4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-content-save</v-icon>
            Review & Save DG Billing Record
          </v-card-title>

          <v-card-text>
            <!-- Complete Record Summary -->
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-subtitle>Billing Period</v-card-subtitle>
                  <v-card-text>
                    <div><strong>Start Date:</strong> {{ formattedStartDate }}</div>
                    <div><strong>End Date:</strong> {{ formattedEndDate }}</div>
                    <div><strong>Duration:</strong> {{ billingDurationDays }} days</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-subtitle>DG Consumption Data</v-card-subtitle>
                  <v-card-text>
                    <div v-if="dgConsumptionData">
                      <div>
                        <strong>Initial Reading:</strong>
                        {{ dgConsumptionData.initialReading }} kWh
                      </div>
                      <div>
                        <strong>Final Reading:</strong>
                        {{ dgConsumptionData.finalReading }} kWh
                      </div>
                      <div>
                        <strong>Total Consumption:</strong>
                        {{ dgConsumptionData.totalConsumption }} kWh
                      </div>
                    </div>
                    <div v-else class="grey--text">
                      No DG consumption data available
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-3">
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-subtitle>Manual Data</v-card-subtitle>
                  <v-card-text>
                    <div>
                      <strong>Diesel Consumption:</strong>
                      {{ manualData.dieselConsumption }} L
                    </div>
                    <div>
                      <strong>Running Hours:</strong>
                      {{ manualData.runningHours }} hrs
                    </div>
                    <div v-if="manualData.notes">
                      <strong>Notes:</strong>
                      {{ manualData.notes }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-subtitle>Efficiency Metrics</v-card-subtitle>
                  <v-card-text>
                    <div v-if="efficiencyMetrics && manualData.dieselConsumption > 0">
                      <div>
                        <strong>kWh per Litre:</strong>
                        {{ efficiencyMetrics.kwhPerLitre.toFixed(2) }}
                      </div>
                      <div>
                        <strong>Litres per Hour:</strong>
                        {{ efficiencyMetrics.litresPerHour.toFixed(2) }}
                      </div>
                    </div>
                    <div v-else class="grey--text">
                      Cannot calculate efficiency (diesel consumption is zero)
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Save Button -->
            <v-row class="mt-4">
              <v-col cols="12" class="text-center">
                <v-btn
                  color="success"
                  large
                  :loading="saving"
                  :disabled="!canSaveRecord"
                  @click="handleSaveDGBillingRecord"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ saving ? 'Saving Record...' : 'Save DG Billing Record' }}
                </v-btn>

                <!-- Progress indicator for saving -->
                <div v-if="saving" class="mt-2">
                  <v-progress-linear
                    indeterminate
                    color="success"
                    height="2"
                  />
                  <div class="text-caption grey--text text-center mt-1">
                    Saving DG billing record to database...
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Navigation Buttons -->
    <v-row class="mt-4">
      <v-col cols="12" class="text-center">
        <v-btn
          v-if="currentStep > 1"
          color="grey"
          @click="previousStep"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Previous
        </v-btn>

        <v-btn
          v-if="currentStep < 4"
          color="primary"
          class="ml-2"
          :disabled="!canProceedToNextStep"
          @click="nextStep"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <v-btn
          v-if="currentStep === 1"
          color="grey"
          class="ml-2"
          @click="resetForm"
        >
          <v-icon left>mdi-refresh</v-icon>
          Reset
        </v-btn>
      </v-col>
    </v-row>

    <!-- Success/Error Messages -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DGEfficiencyMetrics from '../components/dg/DGEfficiencyMetrics.vue';
import BillingPeriodSelector from '../components/billing/BillingPeriodSelector.vue';
import { DGBillingUtils } from '../types';
import { VALIDATION_RULES } from '../config/validationConfig';
import appConfig from '../config/appConfig';

export default {
  name: 'DGBillingEntry',

  components: {
    DGEfficiencyMetrics,
    BillingPeriodSelector,
  },

  data() {
    return {
      currentStep: 1,
      saving: false,
      editingRecordId: null, // Track if we're editing an existing record

      // Form validation states
      periodFormValid: false,
      manualDataFormValid: false,

      // DG consumption data from API
      dgConsumptionData: null,
      showNoDGDataMessage: false,

      // Manual input data
      manualData: {
        dieselConsumption: 0,
        runningHours: 0,
        notes: '',
      },

      // Efficiency metrics
      efficiencyMetrics: null,

      // Snackbar for messages
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000,
      },

      // Validation rules
      dieselConsumptionRules: VALIDATION_RULES.dieselConsumption(),
      runningHoursRules: VALIDATION_RULES.runningHours(),
      notesRules: VALIDATION_RULES.notes(),
    };
  },

  computed: {
    ...mapGetters('dgBilling', ['loading', 'error', 'allDGBillingRecords']),

    formattedStartDate() {
      return this.dgConsumptionData?.billingPeriod?.startDate ? this.formatDate(this.dgConsumptionData.billingPeriod.startDate) : '';
    },

    formattedEndDate() {
      return this.dgConsumptionData?.billingPeriod?.endDate ? this.formatDate(this.dgConsumptionData.billingPeriod.endDate) : '';
    },

    billingDurationDays() {
      if (!this.dgConsumptionData || !this.dgConsumptionData.billingPeriod) return 0;
      const start = new Date(this.dgConsumptionData.billingPeriod.startDate);
      const end = new Date(this.dgConsumptionData.billingPeriod.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    calculatedDGConsumption() {
      return this.dgConsumptionData ? this.dgConsumptionData.totalConsumption : 0;
    },

    averageDailyConsumption() {
      if (!this.dgConsumptionData || this.billingDurationDays === 0) return 0;
      return Math.round(this.dgConsumptionData.totalConsumption / this.billingDurationDays);
    },

    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1:
          return this.dgConsumptionData !== null;
        case 2:
          return this.manualDataFormValid
            && this.manualData.dieselConsumption > 0
            && this.manualData.runningHours > 0;
        case 3:
          return this.efficiencyMetrics !== null;
        case 4:
          return false; // Final step
        default:
          return false;
      }
    },

    canSaveRecord() {
      return this.dgConsumptionData
             && this.dgConsumptionData.billingPeriod
             && this.dgConsumptionData.billingPeriod.startDate
             && this.dgConsumptionData.billingPeriod.endDate
             && this.manualData.dieselConsumption > 0
             && this.manualData.runningHours > 0;
    },

    manualDataValidationErrors() {
      const errors = [];

      // Validate diesel consumption
      const dieselValidation = DGBillingUtils.validateDieselConsumption(
        this.manualData.dieselConsumption,
      );
      if (!dieselValidation.isValid) {
        errors.push(...dieselValidation.errors);
      }

      // Validate running hours
      const hoursValidation = DGBillingUtils.validateRunningHours(this.manualData.runningHours);
      if (!hoursValidation.isValid) {
        errors.push(...hoursValidation.errors);
      }

      return errors;
    },
  },

  watch: {
    // No longer needed since BillingPeriodSelector handles this
  },

  methods: {
    ...mapActions('dgBilling', ['fetchDGConsumptionData', 'saveDGBillingRecord']),

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    onDGConsumptionDataRetrieved(data) {
      // Handle both electricity and DG data formats
      if (data.dgConsumptionData) {
        // DG format
        this.dgConsumptionData = data.dgConsumptionData;
      } else if (data.consumptionData) {
        // Electricity format - map to DG format
        this.dgConsumptionData = {
          totalConsumption: data.consumptionData.totalConsumption || 0,
          initialReading: data.consumptionData.initialReading || 0,
          finalReading: data.consumptionData.finalReading || 0,
          billingPeriod: data.billingPeriod || data.consumptionData.billingPeriod,
          dataSource: data.consumptionData.dataSource || 'manual',
        };
      } else {
        // Direct data format
        this.dgConsumptionData = data;
      }

      this.showNoDGDataMessage = false;

      this.showMessage({
        type: 'success',
        message: `DG consumption data retrieved: ${this.dgConsumptionData.totalConsumption} kWh (${this.dgConsumptionData.dataSource || 'manual'} mode)`,
      });
    },

    onDGConsumptionDataCleared() {
      this.dgConsumptionData = null;
      this.efficiencyMetrics = null;
      this.showNoDGDataMessage = false;
    },

    onManualDataChange() {
      // Trigger efficiency recalculation when manual data changes
      this.$nextTick(() => {
        if (this.currentStep >= 3) {
          // Efficiency metrics will be recalculated automatically by the component
        }
      });
    },

    onEfficiencyCalculated(metrics) {
      this.efficiencyMetrics = metrics;
    },

    async handleSaveDGBillingRecord() {
      if (!this.canSaveRecord) {
        const missingFields = [];
        const missingBillingPeriod = !this.dgConsumptionData?.billingPeriod?.startDate
          || !this.dgConsumptionData?.billingPeriod?.endDate;

        if (missingBillingPeriod) {
          missingFields.push('billing period');
        }
        if (!this.manualData.dieselConsumption || this.manualData.dieselConsumption <= 0) {
          missingFields.push('diesel consumption');
        }
        if (!this.manualData.runningHours || this.manualData.runningHours <= 0) {
          missingFields.push('running hours');
        }

        this.showMessage({
          type: 'error',
          message: `Cannot save record. Please provide: ${missingFields.join(', ')}`,
        });
        return;
      }

      // Validate form data before saving
      if (this.manualDataValidationErrors.length > 0) {
        this.showMessage({
          type: 'error',
          message: `Please fix validation errors: ${this.manualDataValidationErrors.join(', ')}`,
        });
        return;
      }

      this.saving = true;

      try {
        const recordData = {
          id: this.editingRecordId, // Include ID if editing existing record
          billingPeriod: this.dgConsumptionData.billingPeriod,
          dgConsumptionData: this.dgConsumptionData,
          dieselConsumption: this.manualData.dieselConsumption,
          runningHours: this.manualData.runningHours,
          efficiencyMetrics: this.efficiencyMetrics || {
            kwhPerLitre: 0,
            litresPerHour: 0,
          },
          notes: this.manualData.notes || '', // Ensure notes are always included
          createdBy: appConfig.getParentUserName(),
          createdAt: this.editingRecordId ? undefined : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const savedRecord = await this.saveDGBillingRecord(recordData);

        this.showMessage({
          type: 'success',
          message: 'DG billing record saved successfully! '
            + `Record ID: ${savedRecord.id || savedRecord._id}`,
        });

        // Redirect to DG billing history after a delay
        setTimeout(() => {
          this.$router.push('/dg-billing-history');
        }, 2000);
      } catch (error) {
        console.error('Error saving DG billing record:', error);

        // Provide user-friendly error messages
        let errorMessage = 'Failed to save DG billing record';

        if (error.message) {
          if (error.message.includes('network') || error.message.includes('connection')) {
            errorMessage = 'Network connection failed. Please check your internet connection and try again.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Save operation timed out. Please try again.';
          } else if (error.message.includes('validation') || error.message.includes('required')) {
            errorMessage = error.message;
          } else if (error.message.includes('already exists')) {
            errorMessage = 'A DG billing record for this period already exists. Please check your dates.';
          } else if (error.message.includes('permission') || error.message.includes('403')) {
            errorMessage = 'Access denied. You do not have permission to save DG billing records.';
          } else if (error.message.length < 100) {
            errorMessage = error.message;
          }
        }

        this.showMessage({
          type: 'error',
          message: errorMessage,
        });
      } finally {
        this.saving = false;
      }
    },

    nextStep() {
      if (this.canProceedToNextStep) {
        this.currentStep += 1;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        if (this.currentStep === 2) {
          this.onDGConsumptionDataCleared();
        }
        this.currentStep -= 1;
      }
    },

    resetForm() {
      this.currentStep = 1;
      this.editingRecordId = null;
      this.dgConsumptionData = null;
      this.showNoDGDataMessage = false;
      this.manualData = {
        dieselConsumption: 0,
        runningHours: 0,
        notes: '',
      };
      this.efficiencyMetrics = null;

      // Reset the BillingPeriodSelector component
      if (this.$refs.dgPeriodSelector) {
        this.$refs.dgPeriodSelector.reset();
      }

      // Reset form validations
      if (this.$refs.manualDataForm) {
        this.$refs.manualDataForm.resetValidation();
      }

      this.showMessage({
        type: 'info',
        message: 'Form has been reset',
      });
    },

    showMessage({ type, message }) {
      let color = 'success';
      if (type === 'error') {
        color = 'error';
      } else if (type === 'warning') {
        color = 'warning';
      } else if (type === 'info') {
        color = 'info';
      }

      this.snackbar = {
        show: true,
        message,
        color,
        timeout: type === 'error' ? 8000 : 5000,
      };
    },
  },
};
</script>

<style scoped>
.font-weight-bold input {
  font-weight: bold !important;
}

.v-stepper {
  box-shadow: none !important;
}

.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
